import { use } from "react";
import { useInvalidateOrganization } from "@/hooks/use-invalidate-organization";
import {
  AuthUIContext,
  DeleteOrganizationCard,
  OrganizationLogoCard,
  useCurrentOrganization,
} from "@papier/auth-ui";
import { createFileRoute } from "@tanstack/react-router";
import {
  OrganizationPageHeader,
  organizationCardStyles,
} from "@/components/orgs/organization-page";
import { OrganizationProfileForm } from "@/components/orgs/organization-profile-form";
import { PendingApiKeysComponent } from "@/components/pending";
import { SettingsSection } from "@papier/ui/components/ui/settings-section";

export const Route = createFileRoute("/_app/organization/settings")({
  component: OrganizationSettingsPage,
  head: () => ({ meta: [{ title: "Papier - General settings" }] }),
});

function OrganizationSettingsPage() {
  const invalidateOrganization = useInvalidateOrganization();
  const { data: organization, isPending } = useCurrentOrganization();
  const { organization: options } = use(AuthUIContext);
  if (isPending) return <PendingApiKeysComponent />;
  if (!organization) return <p role="status">Choose an organization to manage its settings.</p>;
  return (
    <main key={organization.id} className="w-full min-w-0 space-y-8 sm:space-y-10">
      <OrganizationPageHeader
        title="General settings"
        description="Manage your organization's identity and the details shared by your team."
      />
      <SettingsSection
        id="organization-profile"
        title="Organization profile"
        description="Help your team recognize the workspace and keep its identity up to date."
      >
        <OrganizationProfileForm
          key={organization.id}
          organization={organization}
          onSuccess={invalidateOrganization}
        />
      </SettingsSection>
      {options?.logo && (
        <SettingsSection
          id="organization-branding"
          title="Branding"
          description="A familiar logo helps people choose the right workspace. Logo changes are saved immediately."
        >
          <OrganizationLogoCard classNames={organizationCardStyles} />
        </SettingsSection>
      )}
      <SettingsSection
        id="organization-danger"
        title="Danger zone"
        description="Permanent actions that affect everyone in this organization."
      >
        <DeleteOrganizationCard
          localization={{
            DELETE_ORGANIZATION_DESCRIPTION: `Permanently delete ${organization.name}, all of its projects, and their data. This cannot be undone.`,
          }}
          onSuccess={invalidateOrganization}
        />
      </SettingsSection>
    </main>
  );
}
